import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useWatch } from 'react-hook-form';
import { parseOptionalNumber } from '../../features/audit/fixOcr/utils/vendingTotalsUtils';
import { getInventorySelectedPackSlots } from '../../features/audit/fixOcr/utils/vendingInventoryPackUtils';

type VendingInventoryFormTotalsRowProps = {
  control: any;
  gamesData: Map<string, number> | null;
};

const VendingInventoryFormTotalsRow: React.FC<VendingInventoryFormTotalsRowProps> = React.memo(({ control, gamesData }) => {
  const watchedLines = useWatch({
    control,
    name: 'parsed_data.final_json.lines',
  });

  const { totalQuantity, totalAmount } = useMemo(() => {
    const lines = Array.isArray(watchedLines) ? watchedLines : [];

    return lines.reduce((accumulator: { totalQuantity: number; totalAmount: number }, line: any) => {
      const gameId = line?.game != null ? String(line.game).trim() : '';
      const costPerTicket = gameId && gamesData ? Number(gamesData.get(gameId) || 0) : 0;
      const selectedPackSlots = getInventorySelectedPackSlots(line);
      const quantity = selectedPackSlots.reduce((sum, pack) => sum + (parseOptionalNumber(pack.remaining_qty) ?? 0), 0)
        || (parseOptionalNumber(line?.quantity) ?? 0);

      return {
        totalQuantity: accumulator.totalQuantity + quantity,
        totalAmount: accumulator.totalAmount + costPerTicket * quantity,
      };
    }, { totalQuantity: 0, totalAmount: 0 });
  }, [gamesData, watchedLines]);

  return (
    <Box sx={{ bgcolor: '#fff' }}>
      <Box sx={{ height: 8 }} />

      <Box sx={{ height: 1, bgcolor: '#A3AED099' }} />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '64px minmax(196px, 1.08fr) 96px 104px 20px 20px',
          alignItems: 'center',
          px: 0,
          py: 1,
          bgcolor: '#fff',
          columnGap: 0.25,
        }}
      >
        <Box sx={{ textAlign: 'center', px: 0.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#222B45' }}>
            Total
          </Typography>
        </Box>

        <Box />

        <Box sx={{ textAlign: 'right', px: 0.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#222B45' }}>
            {Number.isFinite(totalQuantity) ? String(totalQuantity) : '0'}
          </Typography>
        </Box>

        <Box sx={{ textAlign: 'right', px: 0.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#222B45' }}>
            {Number.isFinite(totalAmount) ? totalAmount.toFixed(2) : '0.00'}
          </Typography>
        </Box>

        <Box />
        <Box />
      </Box>

      <Box sx={{ height: 1, bgcolor: '#A3AED099' }} />

      <Box sx={{ height: 8 }} />
    </Box>
  );
});

VendingInventoryFormTotalsRow.displayName = 'VendingInventoryFormTotalsRow';

export default VendingInventoryFormTotalsRow;
